import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonDecorators = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Decorators: Mastering Wrappers</h1>
                <p className="subtitle">Exploiting Python's first-class functions to extend behavior dynamically. From closures to metaprogramming.</p>
            </div>

            <div className="content-section">
                <h2>1. The Theoretical Foundation</h2>
                <ProTip><p>
                        Imagine you have a plain <strong>Toy</strong> (a function). You want to give it as a gift.
                        You don't break the toy to paint it. Instead, you put it inside a <strong>Shiny Box</strong> (the Decorator).
                        When the child opens it, they get the same Toy, but the experience was enhanced (wrapping paper, bow, card).
                        A Decorator is just a wrapper function that runs some code <em>before</em> and <em>after</em> your original function runs.
                    </p></ProTip>
                <p>
                    Before we touch the `@` syntax, we must understand the core concept: <strong>Functions are Objects</strong>.
                    In Python, functions are "First-Class Citizens". This means:
                </p>
                <ul className="premium-list">
                    <li>You can assign them to variables (`x = my_func`).</li>
                    <li>You can pass them as arguments to other functions.</li>
                    <li>You can return them from other functions.</li>
                    <li>You can define them inside other functions (Nested Functions).</li>
                </ul>
                <p>
                    This capability allows us to use <strong>Higher-Order Functions</strong>functions that operate on other functions. This is the bedrock of functional programming and the mechanism that powers decorators.
                </p>
            </div>

            <div className="content-section">
                <h2>2. The Syntax Sugar</h2>
                <p>
                    A decorator is simply a function that takes a function `f` and returns a function `g`.
                    The `@` symbol is just "Syntactic Sugar" (a shortcut) for this assignment.
                </p>
                <CodeBlock language="python" code={`# The Manual Way (Under the hood)
def my_decorator(func):
    def wrapper():
        print("Something before...")
        func()
        print("Something after...")
    return wrapper

def say_hello():
    print("Hello!")

say_hello = my_decorator(say_hello) #  This is the real magic

# The Pythonic Way (Syntax Sugar)
@my_decorator
def say_hello():
    print("Hello!")`} />
                <p>
                    When Python sees `@my_decorator`, it immediately executes `my_decorator(say_hello)` <strong>at import time</strong> (when the file is loaded), not when the function is called. This is a critical distinction for understanding side effects.
                </p>
            </div>

            <div className="content-section">
                <h2>3. Real-World Pattern: The Timer</h2>
                <p>
                    One of the most common uses for decorators is instrumentation: measuring how long things take without polluting the business logic.
                </p>
                <CodeBlock language="python" code={`import time
import functools
import { ProTip } from '../../components/LessonUI';

def timer(func):
    """Print the runtime of the decorated function"""
    @functools.wraps(func) # We will explain this shortly!
    def wrapper_timer(*args, **kwargs):
        print(f" Starting {func.__name__}...")
        start_time = time.perf_counter() # High precision clock
        
        value = func(*args, **kwargs) # Call the original
        
        end_time = time.perf_counter()
        run_time = end_time - start_time
        print(f"Finished {func.__name__} in {run_time:.4f} secs")
        return value
    return wrapper_timer

@timer
def waste_some_time(num_times):
    for _ in range(num_times):
        sum([i**2 for i in range(10000)])

waste_some_time(1)
# Output:
#  Starting waste_some_time...
# Finished waste_some_time in 0.0023 secs`} />
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: Metadata and `functools.wraps`</h2>
                <p>
                    When you wrap a function, you accidentally hide its identity.
                    If you check `waste_some_time.__name__` on a decorated function without `wraps`, it will return `wrapper_timer`, not `waste_some_time`.
                </p>
                <p>
                    This is bad because:
                </p>
                <ul className="premium-list">
                    <li>Debuggers show confusing names in stack traces.</li>
                    <li>Documentation Generators (Sphinx/Pydoc) can't find the original docstring.</li>
                    <li>Pickling (serialization) might fail.</li>
                </ul>
                <p>
                    <strong>The Fix:</strong> `@functools.wraps(func)` is a decorator <em>for your decorator</em>. It copies the `__module__`, `__name__`, `__qualname__`, `__doc__`, and `__annotations__` from the original function to the new wrapper. Always use it.
                </p>
            </div>

            <div className="content-section">
                <h2>5. Advanced Pattern: Decorators with Arguments</h2>
                <p>
                    What if we want to configure the decorator? For example, `@retry(times=3)` vs `@retry(times=10)`.
                    To do this, we need a <strong>Decorator Factory</strong>. This requires three levels of nested functions.
                </p>
                <CodeBlock language="python" code={`def retry(max_retries=3, delay=1):
    # Layer 1: The Factory (Receives arguments, returns a decorator)
    def decorator(func):
        # Layer 2: The Decorator (Receives function, returns wrapper)
        @functools.wraps(func)
        def wrapper(*args, **kwargs):
            # Layer 3: The Wrapper (Executes logic)
            attempts = 0
            while attempts < max_retries:
                try:
                    return func(*args, **kwargs)
                except Exception as e:
                    attempts += 1
                    print(f" Attempt {attempts} failed: {e}")
                    if attempts == max_retries:
                        raise # Give up
                    time.sleep(delay)
        return wrapper
    return decorator

@retry(max_retries=2, delay=0.5)
def unstable_network_call():
    import random
    if random.random() < 0.8:
        raise ConnectionError("Network flaked out!")
    return "Success Payload"

# It acts like a resilient function now!
print(unstable_network_call())`} />
                <ProTip><p><strong>Heuristic:</strong> If you see parentheses after the decorator name (`@my_dec()`), it's a Factory calling a function. If no parentheses (`@my_dec`), it's the Decorator itself.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>6. Class-Based Decorators</h2>
                <p>
                    Decorators don't strictly have to be functions. They can be Classes!
                    If a class implements `__call__`, instances of it can act like functions.
                    This is often cleaner when your decorator needs to maintain complex state (like a cache or a count).
                </p>
                <CodeBlock language="python" code={`class CountCalls:
    def __init__(self, func):
        functools.update_wrapper(self, func)
        self.func = func
        self.num_calls = 0 # State is persistant!

    def __call__(self, *args, **kwargs):
        self.num_calls += 1
        print(f"Call {self.num_calls} of {self.func.__name__}")
        return self.func(*args, **kwargs)

@CountCalls
def say_hi():
    print("Hi!")

say_hi() # Call 1...
say_hi() # Call 2...`} />
            </div>

            <div className="content-section">
                <h2>7. Comparison: Decorators vs Java Annotations</h2>
                <p>
                    Decorators are often compared to Annotations in Java or Attributes in C#, but they are fundamentally different.
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Feature</th>
                            <th>Python Decorators</th>
                            <th>Java Annotations</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Execution</strong></td>
                            <td>Active. They execute code at import time and wrap the function.</td>
                            <td>Passive. They just add metadata tags.</td>
                        </tr>
                        <tr>
                            <td><strong>Behavior</strong></td>
                            <td>Can completely change or replace the function logic.</td>
                            <td>Requires an external processor (Reflection) to act on them.</td>
                        </tr>
                        <tr>
                            <td><strong>Flexibility</strong></td>
                            <td>Higher. Can implement complex logic (caching, proxies).</td>
                            <td>Lower. Mostly used for configuration/validation.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>8. Applying Multiple Decorators</h2>
                <p>
                    You can stack multiple decorators on a single function (`Onion Architecture`).
                    But be careful: <strong>Order Matters</strong>.
                    They are applied from bottom-to-top (inner-to-outer).
                </p>
                <CodeBlock language="python" code={`@debug
@timer
@auth_required
def database_query():
    pass

# Is equivalent to:
# database_query = debug(timer(auth_required(database_query)))`} />
                <p>
                    If `auth_required` fails and raises an error, `timer` might never run, and `debug` might see the exception.
                    Think of it like nesting dolls. The inner-most decorator runs first on the way IN, and last on the way OUT.
                </p>
            </div>

        </div>
    );
};

export default PythonDecorators;

